import {ReportDetailsSection} from '@src/modules/Reports/components/ViewReport/components/ReportDetailsSection'
import {ReportHeaderSection} from '@src/modules/Reports/components/ViewReport/components/ReportHeaderSection'
import {ReportInput} from '@src/modules/Reports/components/ViewReport/components/ReportInput'
import {Button, Loading} from '@therms/atalaya'
import {
  Alerts,
  ReportAccessWrapper,
  ReportContextProvider,
  RPCDocs,
  UserClientViewingReport,
  useReport,
  useRPCContext,
  useRPCServerEventHandler,
} from '@therms/react-modules'
import {useState} from 'react'

interface ViewReportProps {
  reportId: string
}

function ViewReport({reportId}: ViewReportProps) {
  const {rpcService} = useRPCContext()

  const [formBuilderErrors, setFormBuilderErrors] = useState<
    {[key: string]: string} | undefined
  >()
  const [hasUnsavedFormChanges, setHasUnsavedFormChanges] = useState(false)

  const {data, loading, refresh} = useReport({
    reportId,
  })

  useRPCServerEventHandler('ReportStateUpdated', (evt, payload) => {
    if (reportId === payload.reportId) {
      Alerts.info(
        <div>
          Report state has changed{' '}
          <Button onClick={refresh} subtle variant="main">
            Reload
          </Button>
        </div>,
        {
          toastId: `report-state-updated-${reportId}`,
        },
      )
    }
  })
  useRPCServerEventHandler('ReportUpdated', (evt, payload) => {
    if (reportId === payload.reportId) {
      Alerts.info(
        <div>
          Report state has changed{' '}
          <Button onClick={refresh} subtle variant="main">
            Reload
          </Button>
        </div>,
        {
          toastId: `report-updated-${reportId}`,
        },
      )
    }
  })

  if (!data && loading) return <Loading overlay size="xxl" />

  if (!data)
    return (
      <div className="gap-x-xs text-color-critical inline-flex w-full items-center justify-center">
        <span>There was a problem loading</span>
        <Button onClick={refresh}>Refresh</Button>
      </div>
    )

  const {report, reportType} = data

  const handleSave = async (params: {
    refsByType?: RPCDocs.Reports.GetReport.v1.RefsByType
    date?: string
  }) => {
    const _report: {
      id: string
      refsByType?: RPCDocs.Reports.GetReport.v1.RefsByType
      date?: string
    } = {id: report.id}

    if (params.refsByType) {
      _report.refsByType = params.refsByType
    }

    if (params.date) {
      _report.date = params.date
    }

    rpcService.Reports.UpdateReport.v1
      .call({
        report: _report,
      })
      .catch((err: any) => {
        console.log('rpcService.Reports.UpdateReport.v1() err', err)

        Alerts.error('There was a problem updating the Report', err.message)
      })
      .finally(() => refresh())
  }

  return (
    <ReportContextProvider reportId={reportId}>
      <ReportAccessWrapper>
        <UserClientViewingReport reportId={reportId} />

        <div className="p-xs md:p-base relative">
          <ReportHeaderSection
            formBuilderValidation={formBuilderErrors}
            hasUnsavedFormChanges={hasUnsavedFormChanges}
            loading={loading}
            onSave={handleSave}
            refreshReport={() => {
              refresh()
            }}
            report={report}
            reportType={reportType}
          />

          <ReportDetailsSection
            onSave={handleSave}
            report={report}
            reportType={reportType}
          />

          <div className="my-base border-border border-t" />

          <ReportInput
            formBuilderValidation={formBuilderErrors}
            onFormBuilderValidate={setFormBuilderErrors}
            onUnsavedChanges={setHasUnsavedFormChanges}
            report={report}
            reportType={reportType}
          />
        </div>
      </ReportAccessWrapper>
    </ReportContextProvider>
  )
}

// used for code-splitting
// eslint-disable-next-line import-x/no-default-export
export default ViewReport
export {ViewReport}
